import { z } from 'zod';
import type { TFunction } from 'i18next';
import {
  normalizeAiText,
  resolveSectionAiInstructions,
  type SectionDraft,
} from './lib/sectionDraft';

/**
 * Schema for the brief-template builder's editable surface. Validates the
 * fields shared between Save and Publish (name + description). Section
 * validation is split into a dedicated schema because Save permits an empty
 * sections array while Publish requires at least one row.
 *
 * A factory, not a module-scope const: building the schema at import time would bake
 * in whatever language was active then, and the messages could never follow a change.
 */
export const buildBriefTemplateBuilderFormSchema = (t: TFunction<'validation'>) =>
  z.object({
    name: z.string().trim().min(1, t('knowledgeHub.briefTemplateBuilder.nameIsRequired')),
    description: z
      .string()
      .trim()
      .min(1, t('knowledgeHub.briefTemplateBuilder.descriptionIsRequired')),
  });

export type BriefTemplateBuilderFormData = z.infer<
  ReturnType<typeof buildBriefTemplateBuilderFormSchema>
>;

/**
 * Section-count schema applied only on Publish.
 */
export const buildBriefTemplateBuilderPublishSectionsSchema = (t: TFunction<'validation'>) =>
  z.array(z.unknown()).min(1, t('knowledgeHub.briefTemplateBuilder.atLeastOneSectionIsRequired'));

/**
 * The page-level Save gate for the section drawer. This is DELIBERATELY STRICTER than the
 * server's own `assertAiInstructionsPresent` (see
 * `apps/assets-api/src/brief-templates/assert-ai-instructions-present.ts`), which only rejects
 * a request that explicitly sends `aiInstructions` blank; a payload that omits the key is
 * always accepted, so without this gate a section could be saved with AI on and nothing stored.
 *
 * It judges `resolveSectionAiInstructions` — the same expression the drawer displays and
 * `toCreateDto` sends — so a section whose description supplies the text passes here and
 * arrives at the server carrying that text. Reading the raw field instead is what made a
 * seeded section read filled while Save refused it.
 *
 * Edits are detected on that resolved value too, against `sectionsSnapshot` (the hydrated
 * persisted state the page already holds), because the resolved value is what this Save would
 * change on the server. A section nobody touched resolves identically on both sides and stays
 * grandfathered, which is what keeps the pre-existing estate — every section authored before
 * typed sections is `aiAllowed: true, aiInstructions: null` — savable.
 */
export function sectionsBlockSaveForAiInstructions(
  sections: readonly SectionDraft[],
  sectionsSnapshot: readonly SectionDraft[],
): boolean {
  const snapshotByTempId = new Map(sectionsSnapshot.map((section) => [section.tempId, section]));

  return sections.some((draft) => {
    if (!draft.aiAllowed) return false;

    const persisted = snapshotByTempId.get(draft.tempId);
    const resolved = normalizeAiText(resolveSectionAiInstructions(draft));
    const turnsOnOrEdits =
      persisted === undefined ||
      !persisted.aiAllowed ||
      resolved !== normalizeAiText(resolveSectionAiInstructions(persisted));

    return turnsOnOrEdits && resolved === null;
  });
}
